import { useState, useEffect, useMemo } from 'preact/hooks'
import { useLocation } from 'preact-iso'
import { Search, Users, AlertCircle } from 'lucide-preact'
import { audienceApi } from '@/api/audience'
import { getAudienceCache, setAudienceCache, clearAudienceCache } from '@/state/audienceCache'
import type { AudienceSearchResultDto, AudienceFilters } from '@/types/audience'
import { useLookups } from '@/hooks/useLookups'
import { routes } from '@/utils/routes'
import { BRAND_CLASSES } from '@/constants/theme'

interface AudienceProps {
    selectedCompanyId: string
    selectedWorkspaceId: string
}

function FileStatusPill({ status }: { status: string }) {
    const norm = (status || '').toLowerCase()
    const map: Record<string, string> = {
        queued: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
        processing: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
        imported: 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300',
        completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
        failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
    }
    const cls = map[norm] || 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
    const label = status || 'Unknown'
    return (
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap ${cls}`}
            title={`File Status: ${label}`}
        >
            <span className="opacity-80">File</span>
            <span className="w-1 h-1 rounded-full bg-current opacity-60" />
            <span>{label}</span>
        </span>
    )
}

// Created / enriched / failed / pending, with no success rate. The rate this replaced divided a
// contact count that was always the file total by an error count that was always zero, so it read
// 100% however many rows had actually failed. Pending is what makes a running import legible: the
// counts move on each reload rather than sitting at zero and looking like a dead job.
function AudienceCard({ item, onClick }: { item: AudienceSearchResultDto; onClick?: () => void }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="text-left bg-white dark:bg-slate-800 rounded-lg shadow-md p-6 border border-slate-200 dark:border-slate-700 hover:shadow-lg transition-shadow cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label={`Open audience file ${item.FileName}`}
        >
            <div className="flex items-start justify-between mb-4">
                <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white truncate" title={item.FileName}>{item.FileName}</h3>
                </div>
                <div className="shrink-0">
                    <FileStatusPill status={item.FileStatus || 'Unknown'} />
                </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                    <div className="text-2xl font-bold text-slate-900 dark:text-white flex items-center justify-center gap-2">
                        <Users className="w-5 h-5 text-slate-500" />
                        {item.ContactsCreated.toLocaleString()}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Created</div>
                </div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-green-600 dark:text-green-400">{item.ContactsEnriched.toLocaleString()}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Enriched</div>
                </div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-red-600 dark:text-red-400">{item.ContactsFailed.toLocaleString()}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Failed</div>
                </div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{item.ContactsPending.toLocaleString()}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Pending</div>
                </div>
            </div>
        </button>
    )
}

export function Audience({ selectedCompanyId, selectedWorkspaceId }: AudienceProps) {
    const location = useLocation()
    const [items, setItems] = useState<AudienceSearchResultDto[]>([])
    const [filteredItems, setFilteredItems] = useState<AudienceSearchResultDto[]>([])
    const [loading, setLoading] = useState(false)
    const [hasSearched, setHasSearched] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [filters, setFilters] = useState<AudienceFilters>({
        campaignStatuses: ['Completed'],
        search: ''
    })
    // Snapshot of last applied (server) filters (excluding the live search text which is client-side only)
    const [appliedFilters, setAppliedFilters] = useState<AudienceFilters>({
        campaignStatuses: ['Completed'],
        search: ''
    })
    const { getCampaignStatuses } = useLookups()
    const campaignStatusOptions = getCampaignStatuses()

    // Attempt to hydrate from cache first (one-shot cache only when returning from a detail view)
    useEffect(() => {
        if (!selectedCompanyId || !selectedWorkspaceId) return
        const cached = getAudienceCache(selectedCompanyId, selectedWorkspaceId)
        if (cached) {
            setFilters(cached.filters)
            setAppliedFilters(cached.filters)
            setItems(cached.items)
            setHasSearched(true)
            // Restore scroll position after paint
            requestAnimationFrame(() => {
                if (typeof cached.scrollY === 'number') {
                    window.scrollTo(0, cached.scrollY)
                }
            })
            setLoading(false)
            // Clear immediately so navigating away elsewhere and back resets state unless user clicks a card again
            clearAudienceCache(selectedCompanyId, selectedWorkspaceId)
            return
        }
        // Do not auto search; wait for user action
    }, [selectedCompanyId, selectedWorkspaceId])

    // Only apply live filtering based on search text; other filter controls require pressing Search
    useEffect(() => {
        let next = items
        if (filters.search?.trim()) {
            const q = filters.search.toLowerCase()
            next = next.filter(i => (i.FileName || '').toLowerCase().includes(q))
        }
        setFilteredItems(next)
    }, [items, filters.search])

    // Determine if non-search filters have changed since last applied search
    const filtersDirty = useMemo(() => {
        const strip = (f: AudienceFilters) => ({
            campaignStatuses: f.campaignStatuses || [],
            campaignName: f.campaignName || '',
            createdDateFrom: f.createdDateFrom || '',
            createdDateTo: f.createdDateTo || ''
        })
        return JSON.stringify(strip(filters)) !== JSON.stringify(strip(appliedFilters))
    }, [filters, appliedFilters])

    const loadAudience = async () => {
        if (!selectedCompanyId || !selectedWorkspaceId) return
        setLoading(true)
        setError(null)
        try {
            const resp = await audienceApi.search(selectedCompanyId, selectedWorkspaceId, {
                CampaignStatuses: filters.campaignStatuses,
                CampaignName: filters.campaignName,
                CreatedDateFrom: filters.createdDateFrom,
                CreatedDateTo: filters.createdDateTo
            })
            if (resp.error) setError(resp.error)
            else setItems(resp.data || [])
            // Mark current non-search filters as applied
            setAppliedFilters(f => ({ ...f, campaignStatuses: filters.campaignStatuses, campaignName: filters.campaignName, createdDateFrom: filters.createdDateFrom, createdDateTo: filters.createdDateTo }))
        } catch (e) {
            setError('Failed to load audience')
            console.error(e)
        } finally {
            setLoading(false)
        }
    }

    const handleFilterChange = (f: Partial<AudienceFilters>) => setFilters(prev => ({ ...prev, ...f }))

    const initialLoading = loading && !hasSearched

    if (error) {
        return (
            <div className="p-6">
                <div className="text-center py-12">
                    <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                        <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
                    </div>
                    <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">Error Loading Audience</h3>
                    <p className="text-slate-600 dark:text-slate-300 mb-4">{error}</p>
                    <button
                        onClick={loadAudience}
                        className={`px-4 py-2 rounded-lg transition-colors ${BRAND_CLASSES.BUTTON.PRIMARY}`}
                    >
                        Try Again
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className="p-6">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-8">Audience</h2>
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <p className="text-slate-600 dark:text-slate-300">Manage and track your audience file imports</p>
                </div>
                <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                    <div className="flex flex-col gap-4">
                        <div className="flex gap-4">
                            <button
                                onClick={() => { setHasSearched(true); loadAudience() }}
                                disabled={loading || !selectedCompanyId || !selectedWorkspaceId}
                                className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${BRAND_CLASSES.BUTTON.PRIMARY} ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                                {loading && <div className="w-4 h-4 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />}
                                {!loading && <Search className="w-4 h-4" />}
                                {loading ? 'Searching...' : 'Search'}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    const reset = { campaignStatuses: ['Completed'], search: '' } as AudienceFilters
                                    setFilters(reset)
                                    setAppliedFilters(reset)
                                    setItems([])
                                    setFilteredItems([])
                                    setHasSearched(false)
                                }}
                                disabled={loading}
                                className={`px-4 py-2 rounded-lg transition-colors border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-700 hover:bg-slate-50 dark:hover:bg-slate-600 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                Reset
                            </button>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Campaign Status</label>
                                <select
                                    value={(filters.campaignStatuses && filters.campaignStatuses.length > 0) ? filters.campaignStatuses[0] : ''}
                                    onChange={(e) => {
                                        const val = e.currentTarget.value
                                        handleFilterChange({ campaignStatuses: val ? [val] : [] })
                                    }}
                                    className={`w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 ${BRAND_CLASSES.FOCUS.PRIMARY} focus:border-transparent`}
                                >
                                    <option value="">All Campaign Statuses</option>
                                    {campaignStatusOptions.map(o => (
                                        <option key={o.Key} value={o.Key}>{o.Description}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Campaign Name</label>
                                <input
                                    type="text"
                                    placeholder="Filter by campaign name..."
                                    value={filters.campaignName || ''}
                                    onChange={(e) => handleFilterChange({ campaignName: e.currentTarget.value })}
                                    className={`w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-500 focus:outline-none focus:ring-2 ${BRAND_CLASSES.FOCUS.PRIMARY} focus:border-transparent`}
                                />
                            </div>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Campaign Created Date From</label>
                                <input
                                    type="date"
                                    value={filters.createdDateFrom || ''}
                                    onChange={(e) => handleFilterChange({ createdDateFrom: e.currentTarget.value })}
                                    className={`w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 ${BRAND_CLASSES.FOCUS.PRIMARY} focus:border-transparent`}
                                />
                            </div>
                            <div className="flex-1">
                                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Campaign Created Date To</label>
                                <input
                                    type="date"
                                    value={filters.createdDateTo || ''}
                                    onChange={(e) => handleFilterChange({ createdDateTo: e.currentTarget.value })}
                                    className={`w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 ${BRAND_CLASSES.FOCUS.PRIMARY} focus:border-transparent`}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div>
                    <div className="flex flex-col gap-2 mb-4">
                        <div className="flex items-center justify-between gap-4">
                            <h3 className="text-lg font-medium text-slate-900 dark:text-white">{filteredItems.length} audience file{filteredItems.length !== 1 ? 's' : ''} found</h3>
                            <div className="flex items-center gap-3">
                                {filtersDirty && hasSearched && (
                                    <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" title="Filters changed since last search">
                                        Filters changed
                                    </span>
                                )}
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                                    <input
                                        type="text"
                                        placeholder="Filter results..."
                                        value={filters.search || ''}
                                        onChange={(e) => handleFilterChange({ search: e.currentTarget.value })}
                                        className="w-[30rem] max-w-full pl-10 pr-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-slate-900 dark:text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    {initialLoading && hasSearched && (
                        <div className="flex items-center justify-center py-16">
                            <div className="flex items-center gap-3 text-slate-600 dark:text-slate-300">
                                <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
                                Loading audience...
                            </div>
                        </div>
                    )}
                    {!initialLoading && hasSearched && filteredItems.length === 0 && (
                        <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700">
                            <div className="w-16 h-16 mx-auto mb-4 bg-slate-100 dark:bg-slate-700 rounded-full" />
                            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">No audience files found</h3>
                            <p className="text-slate-600 dark:text-slate-300">Try adjusting your search filters or check back later.</p>
                        </div>
                    )}
                    {!initialLoading && hasSearched && filteredItems.length > 0 && (
                        <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
                            {filteredItems.map(item => (
                                <AudienceCard
                                    key={item.FileImportId}
                                    item={item}
                                    onClick={() => {
                                        // Persist one-shot cache so returning from detail restores state
                                        setAudienceCache(selectedCompanyId, selectedWorkspaceId, {
                                            filters: { ...filters },
                                            items,
                                            timestamp: Date.now(),
                                            scrollY: window.scrollY
                                        })
                                        location.route(
                                            routes.scoped(selectedCompanyId, selectedWorkspaceId).audienceDetail(
                                                item.CampaignId,
                                                item.FileImportId,
                                                { origin: 'audience-index' }
                                            )
                                        )
                                    }}
                                />
                            ))}
                        </div>
                    )}
                    {!hasSearched && !loading && (
                        <div className="text-center py-16 text-slate-600 dark:text-slate-300">
                            <p className="mb-2 font-medium">Start by searching your audience files</p>
                            <p className="text-sm opacity-80">Adjust filters if needed and click Search.</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default Audience
